// src/components/learner/SubmissionForm.js
import React, { useState, useEffect } from 'react';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { uploadAssignmentFile, deleteFile } from '../../utils/storage';

function SubmissionForm({ assignmentId, existingSubmission, onSubmitSuccess }) {
  const { currentUser } = useAuth();

  const [text, setText] = useState('');
  const [existingFiles, setExistingFiles] = useState([]); // files already in Firestore
  const [newFiles, setNewFiles] = useState([]);           // files selected but not yet uploaded
  const [removedFiles, setRemovedFiles] = useState([]);   // existing files marked for deletion
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Populate form from existing submission
  useEffect(() => {
    if (existingSubmission) {
      setText(existingSubmission.content || '');
      setExistingFiles(existingSubmission.fileUrls || []);
    }
  }, [existingSubmission]);

  const handleFileChange = (e) => {
    if (e.target.files) {
      setNewFiles(Array.from(e.target.files));
    }
  };

  const handleRemoveExistingFile = (file) => {
    setRemovedFiles(prev => [...prev, file]);
    setExistingFiles(prev => prev.filter(f => f.url !== file.url));
  };

  const handleRemoveNewFile = (index) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async (readyForReview) => {
    setError(null);
    setSuccess(null);

    if (!text.trim() && existingFiles.length === 0 && newFiles.length === 0) {
      setError("Please add some text or upload at least one file before saving.");
      return;
    }

    setUploading(true);

    try {
      // Delete removed files from storage
      for (const file of removedFiles) {
        await deleteFile(currentUser.uid, assignmentId, file.name);
      }

      // Upload new files
      const uploadedFiles = await Promise.all(
        newFiles.map(file => uploadAssignmentFile(file, currentUser.uid, assignmentId))
      );

      const allFileUrls = [...existingFiles, ...uploadedFiles];

      const submissionData = {
        content: text,
        fileUrls: allFileUrls,
        submissionStatus: readyForReview ? 'submitted' : 'draft',
        readyForReview,
        updatedAt: serverTimestamp()
      };

      if (existingSubmission?.id) {
        // Update existing submission
        await updateDoc(doc(db, "submissions", existingSubmission.id), submissionData);
      } else {
        // Create new submission
        await addDoc(collection(db, "submissions"), {
          ...submissionData,
          learnerId: currentUser.uid,
          assignmentId,
          reviewStatus: 'pending',
          createdAt: serverTimestamp()
        });
      }

      setNewFiles([]);
      setRemovedFiles([]);
      setExistingFiles(allFileUrls); 
      setSuccess(readyForReview ? 'Submission sent to your mentor for review.' : 'Draft saved.');

      if (onSubmitSuccess) onSubmitSuccess();
    } catch (err) {
      console.error("Error saving submission:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const isAlreadyReviewed = existingSubmission?.reviewStatus === 'reviewed';

  return (
    <div className="submission-form">
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="form-group">
        <label htmlFor="submission-text">Your Answer</label>
        <textarea
          id="submission-text"
          className="form-control"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          placeholder="Type your answer here..."
        />
      </div>

      {/* Existing uploaded files */}
      {existingFiles.length > 0 && (
        <div className="form-group">
          <label>Uploaded Files</label>
          <ul className="file-list">
            {existingFiles.map((file, i) => (
              <li key={i} className="file-item">
                <div className="file-info">
                  <a href={file.url} target="_blank" rel="noopener noreferrer" className="file-name">
                    {file.name}
                  </a>
                  <span className="file-type">{file.type}</span>
                </div>
                <button
                  type="button"
                  className="remove-file-btn"
                  onClick={() => handleRemoveExistingFile(file)}
                  disabled={uploading}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* New file picker */}
      <div className="form-group file-upload-section">
        <label className="file-upload-label" htmlFor="file-upload">
          + Add Files
        </label>
        <input
          id="file-upload"
          className="file-input"
          type="file"
          multiple
          onChange={handleFileChange}
          disabled={uploading}
        />
        {newFiles.length > 0 && (
          <ul className="file-list">
            {newFiles.map((file, i) => (
              <li key={i} className="file-item">
                <span className="file-name">{file.name}</span>
                <button
                  type="button"
                  className="remove-file-btn"
                  onClick={() => handleRemoveNewFile(i)}
                  disabled={uploading}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {isAlreadyReviewed && (
        <div className="resubmission-alert">
          <p><strong>This submission has been reviewed by your mentor.</strong></p>
          <p>You can make changes and resubmit for additional feedback.</p>
        </div>
      )}

      <div className="form-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => handleSave(false)}
          disabled={uploading}
        >
          {uploading ? 'Saving...' : 'Save Draft'}
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={() => handleSave(true)}
          disabled={uploading}
        >
          {uploading ? 'Submitting...' : isAlreadyReviewed ? 'Resubmit for Review' : 'Submit for Review'}
        </button>
      </div>
    </div>
  );
}

export default SubmissionForm;